/**
 * NotificationPreferences - Refined Oracle Strategy UI
 * 
 * Comprehensive notification settings with:
 * - Push notification toggles (daily horoscope, transits, oracle reminders, moon phases)
 * - Email toggles (weekly digest, major transits, re-engagement, solar return, golden windows)
 * - Timezone selection with auto-detect
 * - Quiet hours configuration
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, Mail, Moon, Sun, Clock, Globe, 
  CheckCircle, Loader2, ChevronDown, ChevronUp,
  Smartphone, MessageSquare, Star, Sparkles, Calendar
} from 'lucide-react';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { getAuthHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Common timezones organized by region
const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time (US)", offset: "-5:00" },
  { value: "America/Chicago", label: "Central Time (US)", offset: "-6:00" },
  { value: "America/Denver", label: "Mountain Time (US)", offset: "-7:00" },
  { value: "America/Los_Angeles", label: "Pacific Time (US)", offset: "-8:00" },
  { value: "America/Mexico_City", label: "Mexico City", offset: "-6:00" },
  { value: "America/Bogota", label: "Colombia", offset: "-5:00" },
  { value: "America/Lima", label: "Peru", offset: "-5:00" },
  { value: "America/Santiago", label: "Chile", offset: "-4:00" },
  { value: "America/Buenos_Aires", label: "Argentina", offset: "-3:00" },
  { value: "America/Sao_Paulo", label: "Brazil", offset: "-3:00" },
  { value: "Europe/London", label: "London (UK)", offset: "+0:00" },
  { value: "Europe/Paris", label: "Paris (CET)", offset: "+1:00" },
  { value: "Europe/Berlin", label: "Berlin (CET)", offset: "+1:00" },
  { value: "Europe/Madrid", label: "Madrid (CET)", offset: "+1:00" },
  { value: "Asia/Dubai", label: "Dubai (UAE)", offset: "+4:00" },
  { value: "Asia/Kolkata", label: "India", offset: "+5:30" },
  { value: "Asia/Singapore", label: "Singapore", offset: "+8:00" },
  { value: "Asia/Tokyo", label: "Tokyo (Japan)", offset: "+9:00" },
  { value: "Australia/Sydney", label: "Sydney (Australia)", offset: "+11:00" },
];

// Preference configurations
const PUSH_PREFERENCES = {
  push_daily_horoscope: {
    icon: Sun,
    title: { en: "Daily Horoscope", es: "Horóscopo Diario" },
    description: { 
      en: "Morning cosmic guidance based on your chart", 
      es: "Guía cósmica matutina basada en tu carta" 
    }
  },
  push_transit_alerts: {
    icon: Sparkles,
    title: { en: "Transit Alerts", es: "Alertas de Tránsitos" },
    description: { 
      en: "Real-time notifications when planets affect your chart", 
      es: "Notificaciones en tiempo real cuando los planetas afectan tu carta" 
    }
  },
  push_oracle_reminder: {
    icon: MessageSquare,
    title: { en: "Oracle Reminders", es: "Recordatorios del Oráculo" },
    description: { 
      en: "Gentle nudges when The Oracle has insights for you", 
      es: "Sutiles recordatorios cuando El Oráculo tiene perspectivas para ti" 
    }
  },
  push_moon_phases: {
    icon: Moon,
    title: { en: "Moon Phase Alerts", es: "Alertas de Fases Lunares" },
    description: { 
      en: "New Moon and Full Moon notifications", 
      es: "Notificaciones de Luna Nueva y Luna Llena" 
    }
  }
};

const EMAIL_PREFERENCES = {
  email_weekly_digest: {
    icon: Mail,
    title: { en: "Weekly Cosmic Digest", es: "Resumen Cósmico Semanal" },
    description: { 
      en: "Every Monday: your personalized forecast for the week", 
      es: "Cada lunes: tu pronóstico personalizado para la semana" 
    }
  },
  email_major_transits: {
    icon: Star,
    title: { en: "Major Transit Alerts", es: "Alertas de Tránsitos Mayores" },
    description: { 
      en: "Mercury retrograde, eclipses, and significant alignments", 
      es: "Mercurio retrógrado, eclipses y alineaciones significativas" 
    }
  },
  email_golden_windows: {
    icon: Sparkles,
    title: { en: "Golden Window Opportunities", es: "Oportunidades de Ventana Dorada" },
    description: { 
      en: "When favorable alignments occur for your connections", 
      es: "Cuando ocurren alineaciones favorables para tus conexiones" 
    }
  },
  email_solar_return: {
    icon: Calendar,
    title: { en: "Solar Return (Birthday)", es: "Retorno Solar (Cumpleaños)" },
    description: { 
      en: "Annual birthday celebration from The Oracle", 
      es: "Celebración anual de cumpleaños del Oráculo" 
    }
  },
  email_reengagement: {
    icon: Bell,
    title: { en: "Comeback Reminders", es: "Recordatorios de Regreso" },
    description: { 
      en: "Gentle reminders if you've been away (max 3, then we stop)", 
      es: "Recordatorios sutiles si has estado ausente (máximo 3, luego paramos)" 
    }
  }
};

const NotificationPreferences = ({ language = 'en', onClose = null }) => {
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [pushExpanded, setPushExpanded] = useState(true);
  const [emailExpanded, setEmailExpanded] = useState(true);
  const [quietHoursExpanded, setQuietHoursExpanded] = useState(false);

  // Fetch current preferences on mount
  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/users/me/preferences`, {
        headers,
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreferences(data);
        
        // Auto-detect timezone if not set
        if (!data.timezone) {
          const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (TIMEZONE_OPTIONS.find(tz => tz.value === detectedTz)) {
            setPreferences(prev => ({ ...prev, timezone: detectedTz }));
            setHasChanges(true);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
      toast.error(language === 'es' ? 'Error al cargar preferencias' : 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setHasChanges(true);
  };

  const handleTimezoneChange = (e) => {
    setPreferences(prev => ({
      ...prev,
      timezone: e.target.value
    }));
    setHasChanges(true);
  };

  const handleQuietHoursChange = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: parseInt(value, 10)
    }));
    setHasChanges(true);
  };

  const savePreferences = async () => {
    setSaving(true);
    
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/users/me/preferences`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(preferences)
      });
      
      if (response.ok) {
        toast.success(
          language === 'es' 
            ? '✨ Preferencias guardadas' 
            : '✨ Preferences saved'
        );
        setHasChanges(false);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Failed to save preferences:', error);
      toast.error(
        language === 'es' 
          ? 'Error al guardar preferencias' 
          : 'Failed to save preferences'
      );
    } finally {
      setSaving(false);
    }
  };

  const enableAllPush = () => {
    const updates = {};
    Object.keys(PUSH_PREFERENCES).forEach(key => {
      updates[key] = true;
    });
    setPreferences(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const enableAllEmail = () => {
    const updates = {};
    Object.keys(EMAIL_PREFERENCES).forEach(key => {
      updates[key] = true;
    });
    setPreferences(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const disableAllPush = () => {
    const updates = {};
    Object.keys(PUSH_PREFERENCES).forEach(key => {
      updates[key] = false;
    });
    setPreferences(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const disableAllEmail = () => {
    const updates = {};
    Object.keys(EMAIL_PREFERENCES).forEach(key => {
      updates[key] = false;
    });
    setPreferences(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  // Render preference item
  const renderPreferenceItem = (key, config, value) => {
    const Icon = config.icon;
    const isEnabled = value !== false;
    
    return (
      <motion.div
        key={key}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className={`flex items-center justify-between p-4 rounded-xl transition-all
                   ${isEnabled 
                     ? 'bg-[#D4AF37]/5 border border-[#D4AF37]/20' 
                     : 'bg-white/5 border border-white/10'}`}
        data-testid={`preference-${key}`}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0
                         ${isEnabled ? 'bg-[#D4AF37]/20' : 'bg-white/10'}`}>
            <Icon className={`w-5 h-5 ${isEnabled ? 'text-[#D4AF37]' : 'text-white/50'}`} />
          </div>
          <div>
            <h4 className={`font-medium text-sm ${isEnabled ? 'text-white' : 'text-white/70'}`}>
              {config.title[language] || config.title.en}
            </h4>
            <p className="text-xs text-white/50 mt-0.5 leading-relaxed">
              {config.description[language] || config.description.en}
            </p>
          </div>
        </div>
        
        <Switch
          checked={isEnabled}
          onCheckedChange={() => handleToggle(key)}
          className="data-[state=checked]:bg-[#D4AF37] shrink-0 ml-4"
          data-testid={`toggle-${key}`}
        />
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-b from-[#12151c] to-[#0b0e14] rounded-2xl border border-[#D4AF37]/20 p-6 max-h-[80vh] overflow-y-auto"
      data-testid="notification-preferences-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
            <Bell className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-white">
              {language === 'es' ? 'Preferencias de Notificación' : 'Notification Preferences'}
            </h3>
            <p className="text-sm text-white/50">
              {language === 'es' 
                ? 'Controla cómo te contacta el Oráculo' 
                : 'Control how The Oracle reaches you'}
            </p>
          </div>
        </div>
        
        {onClose && (
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white transition-colors"
            data-testid="close-preferences-btn"
          >
            ✕
          </button>
        )}
      </div>

      {/* Push Notifications Section */}
      <div className="mb-6">
        <button
          onClick={() => setPushExpanded(!pushExpanded)}
          className="w-full flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors mb-3"
          data-testid="push-section-toggle"
        >
          <div className="flex items-center gap-3">
            <Smartphone className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-white font-medium">
              {language === 'es' ? 'Notificaciones Push' : 'Push Notifications'}
            </span>
          </div>
          {pushExpanded ? (
            <ChevronUp className="w-5 h-5 text-white/50" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/50" />
          )}
        </button>
        
        <AnimatePresence>
          {pushExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              {/* Quick actions */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={enableAllPush}
                  className="flex-1 py-1.5 px-3 rounded-lg bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 
                             text-[#D4AF37] text-xs transition-colors"
                  data-testid="enable-all-push"
                >
                  {language === 'es' ? 'Activar todo' : 'Enable all'}
                </button>
                <button
                  onClick={disableAllPush}
                  className="flex-1 py-1.5 px-3 rounded-lg bg-white/5 hover:bg-white/10 
                             text-white/70 text-xs transition-colors"
                  data-testid="disable-all-push"
                >
                  {language === 'es' ? 'Desactivar todo' : 'Disable all'}
                </button>
              </div>
              
              <div className="space-y-3">
                {Object.entries(PUSH_PREFERENCES).map(([key, config]) => 
                  renderPreferenceItem(key, config, preferences[key])
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Email Section */}
      <div className="mb-6">
        <button
          onClick={() => setEmailExpanded(!emailExpanded)}
          className="w-full flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors mb-3"
          data-testid="email-section-toggle"
        >
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-white font-medium">
              {language === 'es' ? 'Correos Electrónicos' : 'Email Communications'}
            </span>
          </div>
          {emailExpanded ? (
            <ChevronUp className="w-5 h-5 text-white/50" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/50" />
          )}
        </button>
        
        <AnimatePresence>
          {emailExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              {/* Quick actions */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={enableAllEmail}
                  className="flex-1 py-1.5 px-3 rounded-lg bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 
                             text-[#D4AF37] text-xs transition-colors"
                  data-testid="enable-all-email"
                >
                  {language === 'es' ? 'Activar todo' : 'Enable all'}
                </button>
                <button
                  onClick={disableAllEmail}
                  className="flex-1 py-1.5 px-3 rounded-lg bg-white/5 hover:bg-white/10 
                             text-white/70 text-xs transition-colors"
                  data-testid="disable-all-email"
                >
                  {language === 'es' ? 'Desactivar todo' : 'Disable all'}
                </button>
              </div>
              
              <div className="space-y-3">
                {Object.entries(EMAIL_PREFERENCES).map(([key, config]) => 
                  renderPreferenceItem(key, config, preferences[key])
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Quiet Hours Section */}
      <div className="mb-6">
        <button
          onClick={() => setQuietHoursExpanded(!quietHoursExpanded)}
          className="w-full flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors mb-3"
          data-testid="quiet-hours-toggle"
        >
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-white font-medium">
              {language === 'es' ? 'Horas de Silencio' : 'Quiet Hours'}
            </span>
          </div>
          {quietHoursExpanded ? (
            <ChevronUp className="w-5 h-5 text-white/50" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/50" />
          )}
        </button>
        
        <AnimatePresence>
          {quietHoursExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden space-y-4"
            >
              {/* Timezone */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <Globe className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-white text-sm font-medium">
                    {language === 'es' ? 'Tu Zona Horaria' : 'Your Timezone'}
                  </span>
                </div>
                <select
                  value={preferences.timezone || ''}
                  onChange={handleTimezoneChange}
                  className="w-full bg-[#0b0e14] border border-white/20 rounded-lg p-3 text-white text-sm
                           focus:border-[#D4AF37]/50 focus:outline-none"
                  data-testid="timezone-select"
                >
                  <option value="">
                    {language === 'es' ? '-- Seleccionar zona horaria --' : '-- Select timezone --'}
                  </option>
                  {TIMEZONE_OPTIONS.map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} (UTC{tz.offset})
                    </option>
                  ))}
                </select>
              </div>

              {/* Quiet hours range */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white/60 text-xs mb-4">
                  {language === 'es' 
                    ? 'No enviaremos notificaciones durante estas horas' 
                    : "We won't send notifications during these hours"}
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="text-white/50 text-xs mb-1 block">
                      {language === 'es' ? 'Desde' : 'From'}
                    </label>
                    <select
                      value={preferences.quiet_hours_start ?? 22}
                      onChange={(e) => handleQuietHoursChange('quiet_hours_start', e.target.value)}
                      className="w-full bg-[#0b0e14] border border-white/20 rounded-lg p-2 text-white text-sm
                               focus:border-[#D4AF37]/50 focus:outline-none"
                      data-testid="quiet-start-select"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {i.toString().padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="text-white/30 mt-4">→</span>
                  <div className="flex-1">
                    <label className="text-white/50 text-xs mb-1 block">
                      {language === 'es' ? 'Hasta' : 'Until'}
                    </label>
                    <select
                      value={preferences.quiet_hours_end ?? 8}
                      onChange={(e) => handleQuietHoursChange('quiet_hours_end', e.target.value)}
                      className="w-full bg-[#0b0e14] border border-white/20 rounded-lg p-2 text-white text-sm
                               focus:border-[#D4AF37]/50 focus:outline-none"
                      data-testid="quiet-end-select"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {i.toString().padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-white/40 text-xs mt-3 text-center">
                  {language === 'es' 
                    ? `Silencio de ${(preferences.quiet_hours_start ?? 22).toString().padStart(2, '0')}:00 a ${(preferences.quiet_hours_end ?? 8).toString().padStart(2, '0')}:00`
                    : `Quiet from ${(preferences.quiet_hours_start ?? 22).toString().padStart(2, '0')}:00 to ${(preferences.quiet_hours_end ?? 8).toString().padStart(2, '0')}:00`}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Save Button */}
      {hasChanges && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-4 border-t border-white/10"
        >
          <button
            onClick={savePreferences}
            disabled={saving}
            className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#B8962D]
                       text-[#0b0e14] font-medium flex items-center justify-center gap-2
                       hover:opacity-90 transition-opacity disabled:opacity-50"
            data-testid="save-preferences-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {language === 'es' ? 'Guardando...' : 'Saving...'}
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                {language === 'es' ? 'Guardar Preferencias' : 'Save Preferences'}
              </>
            )}
          </button>
        </motion.div>
      )}

      {/* Info Footer */}
      <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
        <p className="text-xs text-white/40 text-center">
          {language === 'es' 
            ? 'El Oráculo respeta tu silencio. Puedes cambiar estas preferencias en cualquier momento.' 
            : 'The Oracle respects your silence. You can change these preferences at any time.'}
        </p>
      </div>
    </motion.div>
  );
};

export default NotificationPreferences;
